import { getApiBaseUrl } from './domain';

const API_BASE_URL = getApiBaseUrl();
const API_ROOT = API_BASE_URL ? `${API_BASE_URL.replace(/\/+$/, '')}/api/` : '/api/';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request(endpoint: string, options: RequestOptions = {}) {
  const { params, ...init } = options;

  // Build URL with query parameters
  const normalizedEndpoint = endpoint.replace(/^\/+/, '');
  const url = new URL(normalizedEndpoint, API_ROOT);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }

  // Add default headers
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url.toString(), {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      `API request failed: ${response.statusText}`
    );
  }

  // Return null for 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
    return request(endpoint, { ...options, method: 'GET' });
  },

  post: <T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> => {
    return request(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  put: <T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> => {
    return request(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  patch: <T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> => {
    return request(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  delete: <T>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
    return request(endpoint, { ...options, method: 'DELETE' });
  },
};
